import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { usePinPrompt } from "@/components/app/PinPrompt";

const emptyT = { from_warehouse_id: "", from_bin: "", to_warehouse_id: "", to_bin: "", vehicle_number: "", transporter: "", expected_date: "", notes: "" };

const WhSelect = ({ label, wid, bin, whs, onChange, tid, allowNone }) => (
  <>
    <div><Label>{label} warehouse</Label>
      <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={wid} onChange={(e) => onChange(e.target.value, "")} data-testid={`${tid}-wh`}>
        <option value="">{allowNone ? "— unassigned stock —" : "— select —"}</option>
        {whs.map((w) => <option key={w._id_str} value={w._id_str}>{w.name}</option>)}
      </select>
    </div>
    <div><Label>{label} bin</Label>
      <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={bin} onChange={(e) => onChange(wid, e.target.value)} data-testid={`${tid}-bin`}>
        <option value="">— none —</option>
        {(whs.find((w) => w._id_str === wid)?.bins || []).map((b) => <option key={b} value={b}>{b}</option>)}
      </select>
    </div>
  </>
);

export default function TransferDialog({ open, onClose, warehouses, onDone }) {
  const pinPrompt = usePinPrompt();
  const [items, setItems] = useState([]);
  const [f, setF] = useState(emptyT);
  const [lines, setLines] = useState([{ item_id: "", qty: 1 }]);
  useEffect(() => { if (open) { api.get("/items").then(({ data }) => setItems(data.filter((i) => i.item_type !== "service"))); setF(emptyT); setLines([{ item_id: "", qty: 1 }]); } }, [open]);

  const avail = (id) => {
    const it = items.find((x) => x._id_str === id);
    if (!it) return 0;
    if (!f.from_warehouse_id) return it.stock;
    return (it.locations || []).filter((l) => l.warehouse_id === f.from_warehouse_id && (!f.from_bin || l.bin === f.from_bin)).reduce((s, l) => s + l.qty, 0);
  };

  const save = async () => {
    if (!f.to_warehouse_id) return toast.error("Choose destination warehouse");
    const ls = lines.filter((l) => l.item_id && l.qty > 0).map((l) => { const it = items.find((x) => x._id_str === l.item_id); return { item_id: l.item_id, name: it.name, unit: it.unit, qty: +l.qty }; });
    if (!ls.length) return toast.error("Add items");
    await pinPrompt();
    try { const { data } = await api.post("/stock-transfers", { ...f, from_warehouse_id: f.from_warehouse_id || null, lines: ls }); toast.success(`${data.number} dispatched — in transit`); onClose(); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="transfer-dialog">
        <DialogHeader><DialogTitle>Stock Transfer (goes In Transit until received)</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <WhSelect label="From" tid="xfer-from" allowNone wid={f.from_warehouse_id} bin={f.from_bin} whs={warehouses} onChange={(w, b) => setF({ ...f, from_warehouse_id: w, from_bin: b })} />
          <WhSelect label="To" tid="xfer-to" wid={f.to_warehouse_id} bin={f.to_bin} whs={warehouses} onChange={(w, b) => setF({ ...f, to_warehouse_id: w, to_bin: b })} />
          <div><Label>Vehicle</Label><Input value={f.vehicle_number} onChange={(e) => setF({ ...f, vehicle_number: e.target.value })} /></div>
          <div><Label>Transporter</Label><Input value={f.transporter} onChange={(e) => setF({ ...f, transporter: e.target.value })} /></div>
          <div><Label>Expected arrival</Label><Input type="date" value={f.expected_date} onChange={(e) => setF({ ...f, expected_date: e.target.value })} /></div>
          <div><Label>Notes</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <div className="space-y-2 mt-2">
          {lines.map((l, i) => (
            <div key={`xl-${i}`} className="flex gap-2 items-center">
              <select className="flex-1 border border-border rounded-md h-9 px-2 bg-white" value={l.item_id} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, item_id: e.target.value } : x))} data-testid={`xfer-item-${i}`}>
                <option value="">— item —</option>
                {items.map((it) => <option key={it._id_str} value={it._id_str}>{it.name} · avail {avail(it._id_str)}</option>)}
              </select>
              <Input type="number" className="w-24" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, qty: +e.target.value } : x))} data-testid={`xfer-qty-${i}`} />
              <button className="p-1 text-red-600" onClick={() => setLines(lines.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setLines([...lines, { item_id: "", qty: 1 }])}><Plus size={12} /> Add item</Button>
        </div>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="btn-primary" onClick={save} data-testid="xfer-save-btn">Dispatch</Button></div>
      </DialogContent>
    </Dialog>
  );
}
